import { FC, useState, MouseEvent } from 'react';
import { CardSummaryAnalytic } from 'src/presentations/components/molecules/card/cardAnalytic/CardSummaryAnalytic';
import { CardAnalytic } from 'src/presentations/components/molecules/card/cardAnalytic/CardAnalytic';
import { CardSelector } from 'src/presentations/components/molecules/card/cardAnalytic/CardSelector';
import { CardMetricEdit } from 'src/presentations/components/molecules/card/cardAnalytic/CardMetricEdit';
import { CardTheme, MetricConfig, ICardAnalyticItemProps } from 'src/presentations/components/molecules/card/cardAnalytic/types';
import { CO2 } from 'src/presentations/components/atom/unit';

// Icons
import IconCloud from 'src/assets/images/icons/analytic-cloud.svg';
import IconTruck from 'src/assets/images/icons/analytic-truck.svg';
import IconReceipt from 'src/assets/images/icons/new-receipt-text.svg';
import IconChargesLines from 'src/assets/images/icons/analytic-charges-lines.svg';
import IconWeight from 'src/assets/images/icons/analytic-weight.svg';
import IconWeightBlue from 'src/assets/images/icons/analytic-weight-blue.svg';
import IconBox from 'src/assets/images/icons/analytic-box.svg';
import IconWarning from 'src/assets/images/icons/analytic-warning-error.svg';
import { MetricsSelector } from './MetricsSelector';
import { MetricsSelectorPopover } from '../../../molecules/MetricsSelector';
import { CardId } from './types';
import { useAnalyticsData } from '../context/AnalyticsDataContext';

interface MetricsOverviewProps {
  // Props for Chart view - to control which metrics are shown in the chart
  selectedCards?: ICardAnalyticItemProps[];
  setSelectedCards?: React.Dispatch<React.SetStateAction<ICardAnalyticItemProps[]>>;
}

export const MetricsOverview: FC<MetricsOverviewProps> = ({ selectedCards, setSelectedCards }) => {
  const {
    keyMetricsResponse,
    filteredKeyMetricsResponse,

    baseInvoiceData,
    baseShipmentData,
    baseChargeData,

    filteredInvoiceData,
    filteredShipmentData,
    filteredChargeData,
    isKeyMetricsLoading,
    activeFilterCount,
    activeTab,
    keyMetrics,
    cardLoadingStates,
    cardErrorStates,
  } = useAnalyticsData();

  // Selected metrics for Table view only
  const [selectedMetrics, setSelectedMetrics] = useState<CardId[]>([CardId.NO_INVOICES, CardId.NO_SHIPMENTS, CardId.NO_CHARGES, CardId.TOTAL_CO2_EMISSIONS]);

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);

  const handleOpen = (event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handler for card selection in Chart view
  const onCardSelected = (selectedId: string) => {
    if (!setSelectedCards) return;

    setSelectedCards((currVal) => {
      const selectedItem = keyMetrics.find((item) => item.id === selectedId);

      const isExists = currVal.find((item) => item.id === selectedId);
      if (isExists) {
        return currVal.filter((item) => item.id !== selectedId);
      }

      if (new Set([...currVal, selectedItem].map((item) => item.unit)).size <= 3) {
        return [...currVal, selectedItem];
      }

      return [...currVal];
    });
  };

  const baseMetrics = keyMetricsResponse?.keymetrics;
  const filteredMetrics = filteredKeyMetricsResponse?.keymetrics;
  const currency = keyMetricsResponse?.meta?.currency || 'SGD';

  const getNumber = (val: number | undefined | null) => val || 0;

  const getMetricConfig = (id: CardId): MetricConfig | null => {
    switch (id) {
      case CardId.NO_INVOICES:
        return {
          title: 'Invoices',
          icon: IconReceipt,
          totalCount: getNumber(baseInvoiceData?.keymetrics?.noInvoices),
          totalAmount: getNumber(baseInvoiceData?.keymetrics?.totalAmount?.afterTax),
          filteredCount: getNumber(filteredInvoiceData?.keymetrics?.noInvoices),
          filteredAmount: getNumber(filteredInvoiceData?.keymetrics?.totalAmount?.afterTax),
          unit: '#',
          theme: CardTheme.ORANGE,
          currency,
          isIncludeAmout: true,
        };
      case CardId.NO_SHIPMENTS:
        return {
          title: 'Shipments',
          icon: IconTruck,
          totalCount: getNumber(baseShipmentData?.keymetrics?.noShipments),
          totalAmount: getNumber(baseShipmentData?.keymetrics?.totalAmount?.afterTax),
          filteredCount: getNumber(filteredShipmentData?.keymetrics?.noShipments),
          filteredAmount: getNumber(filteredShipmentData?.keymetrics?.totalAmount?.afterTax),
          unit: '#',
          theme: CardTheme.PURPLE,
          currency,
          isIncludeAmout: true,
        };
      case CardId.NO_CHARGES:
        return {
          title: 'Charges',
          icon: IconChargesLines,
          totalCount: getNumber(baseChargeData?.keymetrics?.noCharges),
          totalAmount: getNumber(baseChargeData?.keymetrics?.totalAmount?.afterTax),
          filteredCount: getNumber(filteredChargeData?.keymetrics?.noCharges),
          filteredAmount: getNumber(filteredChargeData?.keymetrics?.totalAmount?.afterTax),
          unit: '#',
          theme: CardTheme.GREEN,
          currency,
          isIncludeAmout: true,
        };
      case CardId.TOTAL_CO2_EMISSIONS:
        return {
          title: (
            <>
              <CO2 /> Emissions
            </>
          ),
          icon: IconCloud,
          totalCount: getNumber(baseShipmentData?.keymetrics?.totalCo2Emissions),
          totalAmount: getNumber(baseShipmentData?.keymetrics?.totalCo2Emissions),
          filteredCount: getNumber(filteredShipmentData?.keymetrics?.totalCo2Emissions),
          filteredAmount: getNumber(filteredShipmentData?.keymetrics?.totalCo2Emissions),
          unit: 't',
          theme: CardTheme.TEAL,
          currency: 't',
          isIncludeAmout: false,
        };
      case CardId.TOTAL_DEVIATION_AMOUNT:
        return {
          title: 'Net Deviations',
          icon: IconWarning,
          totalCount: getNumber(baseChargeData?.keymetrics?.noDeviations),
          totalAmount: getNumber(baseChargeData?.keymetrics?.totalDeviationAmount?.afterTax),
          filteredCount: getNumber(filteredChargeData?.keymetrics?.noDeviations),
          filteredAmount: getNumber(filteredChargeData?.keymetrics?.totalDeviationAmount?.afterTax),
          unit: currency,
          theme: CardTheme.RED,
          currency,
          isIncludeAmout: true,
        };
      case CardId.VOLUME:
        return {
          title: 'Volume',
          icon: IconBox,
          totalCount: getNumber(baseShipmentData?.keymetrics?.volume),
          totalAmount: getNumber(baseShipmentData?.keymetrics?.volume),
          filteredCount: getNumber(filteredShipmentData?.keymetrics?.volume),
          filteredAmount: getNumber(filteredShipmentData?.keymetrics?.volume),
          unit: 'm³',
          theme: CardTheme.BLUE_LIGHT,
          currency: 'm³',
          isIncludeAmout: false,
        };
      case CardId.CHARGABLE_WEIGHT:
        return {
          title: 'Charg. Weight',
          icon: IconWeight,
          totalCount: getNumber(baseShipmentData?.keymetrics?.chargeableWeight),
          totalAmount: getNumber(baseShipmentData?.keymetrics?.chargeableWeight),
          filteredCount: getNumber(filteredShipmentData?.keymetrics?.chargeableWeight),
          filteredAmount: getNumber(filteredShipmentData?.keymetrics?.chargeableWeight),
          unit: 'kg',
          theme: CardTheme.NEUTRAL,
          currency: 'kg',
          isIncludeAmout: false,
        };
      case CardId.GROSS_WEIGHT:
        return {
          title: 'Gross Weight',
          icon: IconWeightBlue,
          totalCount: getNumber(baseShipmentData?.keymetrics?.grossWeight),
          totalAmount: getNumber(baseShipmentData?.keymetrics?.grossWeight),
          filteredCount: getNumber(filteredShipmentData?.keymetrics?.grossWeight),
          filteredAmount: getNumber(filteredShipmentData?.keymetrics?.grossWeight),
          unit: 'kg',
          theme: CardTheme.BLUE,
          currency: 'kg',
          isIncludeAmout: false,
        };
      default:
        return null;
    }
  };

  // ========================================
  // CHART VIEW - Show all cards horizontally with click functionality
  // ========================================
  if (activeTab === 'chart') {
    return (
      <div className='px-16 xl:px-24'>
        <CardSelector onCardSelected={onCardSelected} selectedCards={selectedCards || []} setSelectedCards={setSelectedCards || (() => {})}>
          <div className='flex items-center gap-8 xl:gap-16'>
            {keyMetrics?.map((item) => {
              const cardIsLoading = cardLoadingStates?.[item.id] ?? isKeyMetricsLoading;
              const cardHasError = cardErrorStates?.[item.id] ?? false;

              return (
                <div key={`card-chart-${item.id}`} className='flex-1 min-w-[140px]'>
                  <CardAnalytic item={item} isLoading={cardIsLoading} hasError={cardHasError} cardVariant='chart' />
                </div>
              );
            })}
          </div>
        </CardSelector>
      </div>
    );
  }

  // ========================================
  // TABLE VIEW - Show selected cards with edit button
  // ========================================
  return (
    <div className='px-16 w-full'>
      <div className='flex items-center gap-8 xl:gap-16 w-full'>
        {selectedMetrics && selectedMetrics?.length > 0 && (
          <div className='flex-1 flex items-center justify-between gap-8 xl:gap-16'>
            {selectedMetrics.map((id) => {
              const config = getMetricConfig(id);
              if (!config) return null;

              return (
                <div key={id} className={config.isIncludeAmout ? 'flex-[1.5]' : 'flex-1'}>
                  <CardSummaryAnalytic config={config} filterCount={activeFilterCount} isLoading={isKeyMetricsLoading} />
                </div>
              );
            })}
          </div>
        )}

        {/* Edit Button - Only for Table view */}
        <div className='w-auto'>
          <CardMetricEdit onClick={handleOpen} />
          <MetricsSelectorPopover open={Boolean(anchorEl)} anchorEl={anchorEl} onClose={handleClose}>
            <MetricsSelector
              initialSelected={selectedMetrics}
              onApply={(newSelected) => {
                setSelectedMetrics(newSelected);
                handleClose();
              }}
              onClose={handleClose}
            />
          </MetricsSelectorPopover>
        </div>
      </div>
    </div>
  );
};
